import "~/public/fonts/fonts.css";
import "~/features/app/design-tokens/tokens.css";
import "~/features/app/scripts/iubenda/iubenda.css";
import "~/features/app/scripts/iubenda/iubenda.overrides.css";
import "~/features/app/scripts/iubenda/us-notice.css";
import "~/features/blocks/inventory/marketo-embed-form/form.css";
import "lenis/dist/lenis.css";

import NextHead from "next/head";
import React from "react";
import dynamic from "next/dynamic";
import { type AppProps } from "next/app";
import { LazyMotion, domAnimation } from "framer-motion";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { useRouter } from "next/router";
import type { PageWithLayout } from "~/features/app/types";
import { AppStyles } from "~/features/app/app-styles";
import { SmoothScroll } from "~/features/app/smooth-scroll";
import { PageTransition } from "~/features/app/page-transition";
import { useParamsPersistor } from "~/features/ui/params/use-params-persistor";
import { UTM_PARAMETERS, UTM_STORAGE_KEY } from "~/features/ui/params/utils";
import { GoogleTagManagerScript } from "~/features/app/scripts/gtm";
import { MarkerIoScript } from "~/features/app/scripts/marker-io";
import { WarmlyScript } from "~/features/app/scripts/warmly";
import { IubendaScript } from "~/features/app/scripts/iubenda";

type Props = AppProps & {
  Component: PageWithLayout;
};

const fonts = ["/fonts/Saans-Regular.woff2", "/fonts/AeonikFono-Regular.woff2"];

// Loaded only in Sanity draft mode (see page-context.server.ts). `ssr: false`
// keeps the overlay + @sanity/visual-editing out of the production bundle.
const SanityVisualEditing = dynamic(
  () => import("~/features/sanity/visual-editing").then((mod) => mod.SanityVisualEditing),
  { ssr: false }
);

const App = ({ Component, pageProps }: Props) => {
  const router = useRouter();
  const isDraftMode = Boolean((pageProps as { draftMode?: boolean }).draftMode);
  useParamsPersistor(UTM_PARAMETERS, UTM_STORAGE_KEY);

  React.useEffect(() => {
    router.beforePopState((state) => {
      state.options.scroll = false;
      return true;
    });
  }, [router]);

  React.useEffect(() => {
    // Only run cleanup when a service worker is actually controlling
    // this page — most visitors never had one and shouldn't pay for
    // getRegistrations() / caches.delete() on every navigation.
    if (typeof navigator === "undefined" || !navigator.serviceWorker?.controller) {
      return;
    }
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => registration.unregister());
    });
    if (typeof caches !== "undefined") {
      void caches.delete("vimeo-video-cache");
    }
  }, []);

  return (
    <>
      <NextHead>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" key="viewport" />

        {/* Preconnect to critical third-party origins */}
        {/*
          Iubenda supplies preferences to the application consent controller. A restored choice should land inside
          `wait_for_update` (2s from the denied default) or a consenting
          visitor's first hit is still sent as denied, so warm the connection
          early. The initial denied default and early GTM loading remain independent.
        */}
        <link rel="preconnect" href="https://cdn.iubenda.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://cdn.iubenda.com" />
        <link rel="preconnect" href="https://stream.mux.com" />
        <link rel="preconnect" href="https://image.mux.com" />
        <link rel="dns-prefetch" href="https://stream.mux.com" />
        <link rel="dns-prefetch" href="https://image.mux.com" />

        <link rel="icon" type="image/svg+xml" href="/favicon.svg" crossOrigin="anonymous" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
        {fonts.map((font, idx) => (
          <link key={idx} rel="preload" href={font} as="font" type="font/woff2" crossOrigin="anonymous" />
        ))}
      </NextHead>

      <IubendaScript />
      <GoogleTagManagerScript />
      <MarkerIoScript />
      <WarmlyScript />
      <AppStyles />
      <LazyMotion features={domAnimation} strict>
        <SmoothScroll />
        <TooltipProvider>
          <PageTransition>
            <Component {...pageProps} />
          </PageTransition>
        </TooltipProvider>
      </LazyMotion>
      {isDraftMode ? <SanityVisualEditing /> : null}
    </>
  );
};

export default App;
